// v2/hooks/useCatalog.js — /api/v2/catalog/ (filtreli urun listesi + filtre secenekleri)
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet, qs } from '../utils/api';
import { mapProducts } from '../utils/mapProduct';

const ARAMA_GECIKMESI = 350;

export function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Sayfa filtresi -> API parametreleri (bos olanlar gonderilmez) */
export function apiParams(f) {
  return {
    tab: f.tab === 'frequent' ? 'frequent' : undefined,
    campaign: f.campaign && f.campaign !== 'all' ? f.campaign : undefined,
    search: f.search || undefined,
    stock: f.stock && f.stock !== 'all' ? f.stock : undefined,
    brand: f.brand || undefined,
    model: f.brand && f.model ? f.model : undefined,
    segment: f.segment || undefined,
    engine: f.engine || undefined,
    category: f.category || undefined,
    subcategory: f.category && f.sub ? f.sub : undefined,
  };
}

const BOS_ARAMA = { fuzzy: false, corrected: '', aranan: '' };
const BOS_SONUC = { products: [], totalPages: 1, totalItems: 0, eurRate: 0, aramaBilgi: BOS_ARAMA };

export function useCatalogProducts(f) {
  const search = useDebounced(f.search, ARAMA_GECIKMESI);
  const anahtar = qs({
    ...apiParams({ ...f, search }),
    page: f.page,
    per_page: f.perPage,
    sort: f.sort || undefined,
  });

  const [sonuc, setSonuc] = useState(BOS_SONUC);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const ilk = useRef(true);
  const abortRef = useRef(null);

  const getir = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    if (ilk.current) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const veri = await apiGet(`/v2/catalog/products/${anahtar}`, { signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      const ham = Array.isArray(veri && veri.results) ? veri.results : [];
      const urunler = mapProducts(ham).map((p, i) => ({
        ...p,
        catalogCategory: ham[i].category || '',
        catalogSub: ham[i].subcategory || '',
        bought: ham[i].bought || null,
      }));
      setSonuc({
        products: urunler,
        totalPages: Number(veri && veri.total_pages) || 1,
        totalItems: Number(veri && veri.total_items) || 0,
        eurRate: Number(veri && veri.euro_rate) || 0,
        aramaBilgi: {
          fuzzy: !!(veri && veri.fuzzy),
          corrected: (veri && veri.corrected) || '',
          aranan: (veri && veri.filters && veri.filters.search) || '',
        },
      });
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      setError(e.message || 'Ürünler alınamadı');
      setSonuc(BOS_SONUC);
    } finally {
      if (!ctrl.signal.aborted) {
        ilk.current = false;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [anahtar]);

  useEffect(() => {
    getir();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [getir]);

  return { ...sonuc, loading, refreshing, error, yenile: getir };
}

export function useCatalogFacets(f) {
  const search = useDebounced(f.search, ARAMA_GECIKMESI);
  const anahtar = qs(apiParams({ ...f, search }));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    apiGet(`/v2/catalog/facets/${anahtar}`, { signal: ctrl.signal })
      .then((v) => {
        if (ctrl.signal.aborted) return;
        setData(v);
        setLoading(false);
      })
      .catch((e) => {
        if (e && e.name === 'AbortError') return;
        setError(e.message || 'Filtreler alınamadı');
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [anahtar]);

  // Onceki secenekler yeni cevap gelene kadar ekranda kalir
  return { data, loading, error };
}
